/**
 * 将本地图片文件转为 data URL,供后端 MiniMax 多模态 API 使用。
 * 注意:URL.createObjectURL 生成的 blob: 地址不能发给服务端。
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('无法读取图片文件'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('读取图片失败'));
    reader.readAsDataURL(file);
  });
}

/** 浏览器端压缩并统一为 JPEG,避免超大原图与 HEIC 格式问题 */
export async function compressImageFile(
  file: File,
  maxWidth = 1280,
  quality = 0.85,
): Promise<string> {
  if (typeof document === 'undefined') {
    const raw = await fileToDataUrl(file);
    if (!isImageDataUrl(raw)) throw new Error('图片格式无效,请换一张 JPG/PNG 照片');
    return raw;
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('无法解析图片,请换一张 JPG/PNG 照片'));
      el.src = url;
    });

    const scale = Math.min(1, maxWidth / img.naturalWidth);
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('无法处理图片');
    ctx.drawImage(img, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (!isImageDataUrl(dataUrl)) throw new Error('图片格式无效,请换一张 JPG/PNG 照片');
    return dataUrl;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function isImageDataUrl(url: string): boolean {
  return /^data:image\/(?:jpeg|png|gif|webp);base64,/i.test(url);
}

/** 非 data URL(如 blob:) 时返回可读错误文案 */
export function imageDataUrlError(photo: string | null | undefined): string | null {
  if (!photo) return '请先上传情境照片后再提取线索';
  if (isImageDataUrl(photo)) return null;
  return '图片格式无效,请重新选择照片后提交';
}
